import { useSelector } from 'react-redux';
import { useState } from 'react';
import ScheduleRow from '../../Menus/CalendarDayMenu/Components/ScheduleRow';
import DateDivider from '../../components/DateDivider';
import { getLocalTodayString } from '../../utils/dateFormat';

/**
 * Extract predicted ovulation dates from calendar data
 */
function extractPredictedOvulationDates(calendarData) {
  const predictedOvulationDates = [];

  if (!calendarData) return predictedOvulationDates;

  for (const dateStr in calendarData) {
    const dayData = calendarData[dateStr];
    if (dayData.sharedOvulationPredictions && dayData.sharedOvulationPredictions.length > 0) {
      dayData.sharedOvulationPredictions.forEach(pred => {
        if (pred.ovulationPrediction || pred.ovulationActual) {
          predictedOvulationDates.push({
            ownerId: pred.ownerId,
            ownerName: pred.ownerName,
            ownerImages: pred.ownerImages,
            ovulationActual: pred.ovulationActual,
            ovulationPrediction: pred.ovulationPrediction,
            date: dateStr
          });
        }
      });
    }
  }

  return predictedOvulationDates;
}

/**
 * Group predictions by date
 */
function groupPredictionsByDate(predictions) {
  const groupedByDate = {};
  for (const pred of predictions) {
    if (!groupedByDate[pred.date]) {
      groupedByDate[pred.date] = [];
    }
    groupedByDate[pred.date].push(pred);
  }
  return groupedByDate;
}

/**
 * Calculate new cutoff date (2 weeks earlier)
 */
function calculateEarlierCutoff(currentCutoff) {
  const cutoffDate = new Date(currentCutoff);
  cutoffDate.setDate(cutoffDate.getDate() - 14);
  return `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}-${String(cutoffDate.getDate()).padStart(2, '0')}`;
}

/**
 * OvulationSchedule - Shows upcoming ovulation dates from shared IPs
 */
function OvulationSchedule({ calendarData, todayRef }) {
  const [cutoffDate, setCutoffDate] = useState(null);

  const today = getLocalTodayString();

  // Extract and sort predictions
  const predictedOvulationDates = extractPredictedOvulationDates(calendarData);
  predictedOvulationDates.sort((a, b) => a.date.localeCompare(b.date));

  // Filter by cutoff date
  const effectiveCutoff = cutoffDate || today;
  const displayDates = predictedOvulationDates.filter(pred => pred.date >= effectiveCutoff);

  // Check if there are earlier dates to show
  const hasEarlierDates = predictedOvulationDates.some(pred => pred.date < effectiveCutoff);

  // Group by date
  const groupedByDate = groupPredictionsByDate(displayDates);

  // Ensure today is always in the grouped dates (even if empty)
  if (!groupedByDate[today]) {
    groupedByDate[today] = [];
  }

  const handleShowEarlier = () => {
    const newCutoff = calculateEarlierCutoff(effectiveCutoff);
    setCutoffDate(newCutoff);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
      {hasEarlierDates && (
        <button
          onClick={handleShowEarlier}
          className="w-full mb-4 rounded-lg border border-border bg-muted/50 px-4 py-2 text-xs font-semibold text-foreground hover:bg-muted/80 transition-colors"
        >
          Show Earlier
        </button>
      )}

      {displayDates.length === 0 ? (
        <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
          No upcoming ovulation predictions from shared calendars.
        </div>
      ) : (
        Object.keys(groupedByDate).sort().map((date) => (
          <div key={date} className="mb-6">
            {date === today ? (
              <div ref={todayRef} className="mb-3 flex items-center gap-2">
                <div className="flex-1 h-px bg-[#bfdbfe]" />
                <span className="text-sm font-bold uppercase text-[#bfdbfe]">
                  Today
                </span>
                <div className="flex-1 h-px bg-[#bfdbfe]" />
              </div>
            ) : (
              <DateDivider date={date} />
            )}

            {groupedByDate[date].length > 0 && (
              <ul className="space-y-2">
                {groupedByDate[date].map((pred) => (
                  <li key={`${pred.ownerId}-${pred.date}`}>
                    <ScheduleRow prediction={pred} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default OvulationSchedule;
